import { PageTypes } from 'src/typings/pageTypes'

type DynamicYieldContextInput = {
  pathname?: string
  productSku?: string | null
  cartSkus?: string[]
  categoryPath?: string[]
  pageType?: PageTypes
  branchId?: string | null
}

type DynamicYieldPageContext =
  | { type: 'HOMEPAGE' }
  | { type: 'PRODUCT'; data: string[] }
  | { type: 'CATEGORY'; data: string[] }
  | { type: 'CART'; data: string[] }
  | { type: 'OTHER'; data: string[] }

type DynamicYieldContext = DynamicYieldPageContext & {
  branchId?: string
}

const normalizePath = (pathname = '/') =>
  pathname.replace(/[?#].*$/, '').replace(/\/+$/, '') || '/'

// Le label categoria DY sono stringhe leggibili, non slug URL grezzi.
// Normalizziamo i segmenti rotta: `/cani/cibo-secco` diventa
// `['Cani', 'Cibo Secco']`.
const normalizeDyLabel = (value: string) =>
  value
    .trim()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())

const isValidDySegment = (value: unknown): value is string => {
  if (typeof value !== 'string') return false

  const trimmedValue = value.trim()

  if (!trimmedValue) return false

  const lowerValue = trimmedValue.toLowerCase()

  return (
    // Ignoriamo segmenti dinamici Gatsby non risolti e label home generiche.
    // DY si aspetta dati category path significativi.
    !trimmedValue.includes('[') &&
    !trimmedValue.includes(']') &&
    lowerValue !== 'homepage' &&
    lowerValue !== 'home'
  )
}

const getCategoryPathFromPathname = (pathname: string): string[] =>
  pathname
    .split('/')
    .filter(Boolean)
    .filter((segment) => segment.toLowerCase() !== 'p')
    .filter(isValidDySegment)
    .map(normalizeDyLabel)

const withBranchId = (
  context: DynamicYieldPageContext,
  branchId?: string | null
): DynamicYieldContext => {
  const normalizedBranchId = branchId?.trim()

  return normalizedBranchId
    ? { ...context, branchId: normalizedBranchId }
    : context
}

export const getDynamicYieldContext = ({
  pathname = '/',
  productSku = null,
  cartSkus = [],
  categoryPath = [],
  pageType = PageTypes.NotSet,
  branchId = null,
}: DynamicYieldContextInput): DynamicYieldContext => {
  // Questo e' il mapper unico tra routing/metadati pagina e il contratto
  // recommendationContext di DY. Viene chiamato prima/intorno all'esecuzione
  // degli script DY e poi sui cambi rotta client-side.
  const normalizedPath = normalizePath(pathname)

  const normalizedCategoryPath = (categoryPath ?? [])
    .filter(isValidDySegment)
    .map(normalizeDyLabel)

  const fallbackCategoryPath = getCategoryPathFromPathname(normalizedPath)

  const resolvedCategoryPath =
    normalizedCategoryPath.length > 0
      ? normalizedCategoryPath
      : fallbackCategoryPath

  if (pageType === PageTypes.Home || normalizedPath === '/') {
    // Le campagne homepage di solito sono configurate su questo type DY esatto.
    return withBranchId({ type: 'HOMEPAGE' }, branchId)
  }

  if (pageType === PageTypes.PDP || productSku) {
    // Le recommendation prodotto hanno bisogno dello SKU quando disponibile.
    return withBranchId(
      {
        type: 'PRODUCT',
        data: productSku ? [productSku] : [],
      },
      branchId
    )
  }

  if (pageType === PageTypes.Checkout) {
    // DY tratta recommendation cart/checkout come context CART.
    return withBranchId(
      {
        type: 'CART',
        data: cartSkus,
      },
      branchId
    )
  }

  if (pageType === PageTypes.PLP) {
    // Le campagne PLP usano la gerarchia categoria normalizzata.
    return withBranchId(
      {
        type: 'CATEGORY',
        data: resolvedCategoryPath,
      },
      branchId
    )
  }

  if (normalizedPath.endsWith('/p')) {
    return withBranchId(
      {
        type: 'PRODUCT',
        data: productSku ? [productSku] : [],
      },
      branchId
    )
  }

  if (pageType === PageTypes.InstitutionalPage) {
    return withBranchId({ type: 'OTHER', data: cartSkus }, branchId)
  }

  if (resolvedCategoryPath.length > 0) {
    return withBranchId(
      {
        type: 'CATEGORY',
        data: resolvedCategoryPath,
      },
      branchId
    )
  }

  return withBranchId(
    {
      type: 'OTHER',
      data: [],
    },
    branchId
  )
}
